import {Directive, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild} from '@angular/core';
import { convertToThousands } from '@utils/formatter';
import {coerceNumberProperty} from '@angular/cdk/coercion';

type TextParamss = 'num-only' |
  'num-decimals' |
  'cuit' |
  'ignore' |
  'preventAll' |
  'num-decimals-comma' |
  'no-special-chars' |
  'none';

  const REGEX_NO_SPECIAL_CHARS = /([^\w ])/g;

@Directive({
  selector: '[appInputManager]',
  exportAs: 'inputManager'
})
export class InputManagerDirective {
  @Input('inputTextFilter') params: TextParamss = 'num-only';
  @Input() autofocus: boolean = false;
  @Input() forceAutofocusAttempts: number = 0;
  @Input() maxLength: number = 0;
  @Input() maxLengthText = 0;
  @Output() onEnterKey = new EventEmitter;
  @Input('inputThousandsSeparator') thousandsSeparator = false;
  @Input('inputDisablePaste') disablePaste = false;
  _autofillDecimal = 0;
  @Input() set autofillDecimal(val: number) {
    this._autofillDecimal = coerceNumberProperty(val);
  }

  constructor(private el: ElementRef) { }
  @HostListener('blur')
  public onBlur() {
    window.setTimeout(() => {
      // en el evento blur, verificamos si deberiamos hacer alguna modificacion
      const input = this.el.nativeElement as HTMLInputElement;
      if (!(this._autofillDecimal > 0 && (this.params === 'num-decimals' || this.params === 'num-decimals-comma'))) {
        return;
      }
      let value = input.value;
      if (!(value && value.length)) {
        return;
      }
      if (!value.includes(',')) {
        value += ',';
      }
      const splitted = value.split(',');
      while (splitted[1].length < this._autofillDecimal) {
        splitted[1] += '0';
      }
      input.value = splitted.join(',');
    });
  }

  @HostListener('keypress', ['$event'])
  onKeyDown($event: KeyboardEvent) {
    // enter event
    if ($event.keyCode === 13) {
      this.onEnterKey.emit();
    }

    if (this.params === 'num-only') {
      // number no extras permite solo numeros
      // verificamos el string con un regx
      if (/([^0-9])/g.test($event.key)) {
        // no permitimos ingresar dicha letra
        $event.preventDefault();
      }
    } else if (this.params === 'num-decimals') {
      // solo numeros y decimales
      if (/([^0-9.,])/g.test($event.key)) {
        // no permitimos ingresar dicha letra
        $event.preventDefault();
      }
    } else if (this.params === 'num-decimals-comma') {
      // solo numeros y decimales
      if (/([^0-9,])/g.test($event.key)) {
        // no permitimos ingresar dicha letra
        $event.preventDefault();
      }
    } else if (this.params === 'cuit') {
      // solo numeros y decimales
      if (/([^0-9\-])/g.test($event.key)) {
        // no permitimos ingresar dicha letra
        $event.preventDefault();
      }
    } else if (this.params === 'no-special-chars') {
      // solo numeros y decimales
      if (REGEX_NO_SPECIAL_CHARS.test($event.key)) {
        // no permitimos ingresar dicha letra
        $event.preventDefault();
      }
    }
    else if (this.params === 'preventAll') {
      $event.preventDefault();
    }

    if (this.maxLength !== 0) {
      const value = this.el.nativeElement.value.toString().replace(/\,/g, '').replace(/\./g, '');

      if (value.length + 1 > this.maxLength) {
        $event.preventDefault();
      }
    }

    if (this.maxLengthText > 0) {
      let value = this.el.nativeElement.value.toString();
      if (value.length >= this.maxLengthText) {
        $event.preventDefault();
      }
    }

    // Validar que no permita '00', no mas de 3 decimales despues de la ','
    if (this.thousandsSeparator) {
      const value = `${this.el.nativeElement.value}${$event.key}`.replace(/\./g, '');

      if (!/^(0|([1-9]\d*))(,\d{0,3})?$/.test(value)) {
        $event.preventDefault();
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste($event: ClipboardEvent) {
    $event.preventDefault();

    if (this.disablePaste) return;

    const input = $event.srcElement as HTMLInputElement;
    

    // preparamos los datos
    let pastedInput: string = $event.clipboardData.getData('text/plain');

    if (this.params === 'num-only') {
      pastedInput = pastedInput.replace(/([^0-9])/g, '');
    } else if (this.params === 'num-decimals') {
      pastedInput = pastedInput.replace(/([^0-9.,])/g, '');
    } else if (this.params === 'cuit') {
      pastedInput = pastedInput.replace(/([^0-9\-])/g, '');
    } else if (this.params === 'no-special-chars') {
      pastedInput = pastedInput.replace(REGEX_NO_SPECIAL_CHARS, '');
    }

    if (this.maxLength !== 0) {
      if (pastedInput.length >= this.maxLength) {
        pastedInput = pastedInput.substring(0, this.maxLength);
      }
    }

    /*
    okey boys, esto es mas dificil de lo que esperan
    Dado que la gente esta acostumbrada a usar inputs
    que permiten pegar sobre la seleccion o bien en la posicion del cursor
    no nos queda otra que hacer lo mismo
    */

    let cacheStart = input.selectionStart;

    // paso 1: verificamos si hay seleccion calculando la distancia entre las posiciones de seleccion
    if (Math.abs(input.selectionStart - input.selectionEnd) > 0) {
      // tenemos seleccion, la eliminamos
      input.value = input.value.substring(0, input.selectionStart) + input.value.substring(input.selectionEnd);
      // seteamos la nueva posicion del cursor al inicio de la seleccion
      input.setSelectionRange(cacheStart, cacheStart);
    }

    // posicion actual del cursor, solo para cachear
    const currPos = input.selectionStart;
    // paso 2: pegamos en la posicion del cursor
    input.value = input.value.substring(0, currPos) + pastedInput + input.value.substring(currPos);

    // paso 3: colocamos el cursor en la posicion final del texto pegado
    const index = cacheStart + pastedInput.length;
    input.setSelectionRange(index, index);
    // listo, go get a coffee

    // Se hace update del formControl, ya que sino va a contener el valor antiguo
    const formControl = input.getAttribute('formControlName');
    if (formControl) {
      this.el.nativeElement.dispatchEvent(new Event('input', { bubbles: true })); 
    }
  }

  @HostListener('keyup', ['$event'])
  onKeyUp() {
    if (this.thousandsSeparator) {
      const inputElement = this.el.nativeElement;
      inputElement.value = convertToThousands(inputElement.value);
    }
  }

  ngOnInit() {
    if (this.autofocus) {
      //Otherwise Angular throws error: Expression has changed after it was checked.
      window.setTimeout(() => {
        this.el.nativeElement.focus();
      });
    }
  }

  onBlurTryToFocus() {
    // En ocasiones luego de dibujarse un dialog, se da el caso que ocurre un blur.
    // Usamos este metodo para volver al focus
    if (this.forceAutofocusAttempts > 0) {
      window.setTimeout(() => {
        this.el.nativeElement.focus();
      });
      this.forceAutofocusAttempts--;
    }
  }

}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/utils/input-manager.directive.ts